import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { Send, Flag, Trophy, Shield, Zap, PawPrint } from 'lucide-react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import { getSocket } from '../lib/socket';
import { themeOf, ARENA } from '../lib/classTheme';
import { ArenaFloor, ArenaStatBar, Laurel, Pedestal } from '../components/ArenaFrame';
import {
  ParticleLayer, FloatingNumbers, SlashFX, useScreenFX, UltimateOverlay,
  StatusAura, KOEffect, ComboMeter, HPBar, FocusOrb, GearChips, ItemDropReveal,
} from '../components/CombatFX';
import { SkeletonList } from '../components/ui';

const ZONES = [
  { key: 'head', label: 'Head', icon: '🎯', hint: '×1.35 · −12% acc' },
  { key: 'body', label: 'Body', icon: '🫀', hint: 'Balanced' },
  { key: 'legs', label: 'Legs', icon: '🦵', hint: '×0.80 · slows' },
];

export default function FightRoom() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const userId = useAuth(s => s.user?.id);
  const { fire, FlashLayer, shakeStyle } = useScreenFX();

  const [state, setState] = useState(null);
  const [move, setMove] = useState(null);
  const [target, setTarget] = useState('body');
  const [guard, setGuard] = useState('body');
  const [busy, setBusy] = useState(false);
  const [floats, setFloats] = useState([]);
  const [particle, setParticle] = useState(null);
  const [slash, setSlash] = useState(null);
  const [ult, setUlt] = useState(null);
  const [combo, setCombo] = useState(0);
  const [result, setResult] = useState(null);
  const [drop, setDrop] = useState(null);
  const [chat, setChat] = useState([]);
  const [msg, setMsg] = useState('');
  const logRef = useRef();

  /* ─── refs so the socket effect NEVER re-runs ─── */
  const metaRef = useRef(null);
  const fireRef = useRef(fire);
  const stateRef = useRef(null);
  const joinedRef = useRef(false);

  fireRef.current = fire;
  stateRef.current = state;

  /* ─── static meta, cached forever ─── */
  const { data: meta } = useQuery({
    queryKey: ['heroMeta'],
    queryFn: () => api.get('/hero/meta').then(r => r.data),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  metaRef.current = meta;

  /* ════════════════════════════════════════════
     FX — stable, no deps
     ════════════════════════════════════════════ */
  const pushFloat = useCallback((f) => {
    const item = { ...f, id: Math.random() };
    setFloats(v => [...v, item]);
    setTimeout(() => setFloats(v => v.filter(x => x.id !== item.id)), 1700);
  }, []);

  const playFX = useCallback((events, snapshot) => {
    if (!events?.length) return;

    const isP1 = snapshot?.isP1 ?? stateRef.current?.isP1;
    const attackerIsMe = snapshot ? snapshot.myTurn === false : true;
    const side = attackerIsMe ? 'right' : 'left';
    const myClass = isP1 ? snapshot?.p1?.class_key : snapshot?.p2?.class_key;

    const moveKeyOf = (name) => {
      const m = Object.values(metaRef.current?.moves || {}).find(x => x.name === name);
      return m?.key || 'quick_strike';
    };

    for (const e of events) {
      switch (e.t) {
        case 'hit': {
          const isUlt = e.move === 'ULTIMATE';
          if (isUlt && attackerIsMe) {
            setUlt({
              name: e.actor,
              img: isP1 ? snapshot?.p1?.avatar : snapshot?.p2?.avatar,
              classKey: myClass,
            });
          }
          setSlash({ id: Math.random(), zone: e.zone, move: isUlt ? 'ultimate' : moveKeyOf(e.move) });
          setParticle({
            id: Math.random(),
            type: e.crit ? 'crit' : isUlt ? 'ult' : 'hit',
            x: side === 'right' ? 0.3 : 0.7,
          });
          pushFloat({
            type: e.crit ? 'crit' : 'dmg',
            text: `−${e.damage}`,
            side: side === 'right' ? 'left' : 'right',
          });
          fireRef.current(e.crit ? 'crit' : isUlt ? 'ult' : 'hit', e.crit ? 2 : isUlt ? 2.2 : 1);
          if (attackerIsMe) setCombo(c => c + 1);
          if (navigator.vibrate) navigator.vibrate(e.crit ? [40, 30, 60] : 22);
          break;
        }
        case 'miss':
          pushFloat({ type: 'miss', text: 'MISS', side: side === 'right' ? 'left' : 'right' });
          if (attackerIsMe) setCombo(0);
          break;
        case 'heal':
          setParticle({ id: Math.random(), type: 'heal', x: side === 'right' ? 0.7 : 0.3 });
          pushFloat({ type: 'heal', text: `+${e.amount}`, side });
          fireRef.current('heal', 0.4);
          break;
        case 'pet_assist':
          setParticle({ id: Math.random(), type: 'class', x: side === 'right' ? 0.3 : 0.7 });
          pushFloat({
            type: e.crit ? 'crit' : 'dmg',
            text: `🐾 −${e.damage}`,
            side: side === 'right' ? 'left' : 'right',
          });
          fireRef.current('hit', 0.7);
          break;
        case 'pet_heal':
          setParticle({ id: Math.random(), type: 'heal', x: side === 'right' ? 0.7 : 0.3 });
          pushFloat({ type: 'heal', text: `🐾 +${e.amount}`, side });
          fireRef.current('heal', 0.4);
          break;
        case 'pet_guard':
          pushFloat({ type: 'block', text: '🐾 SHIELD', side });
          fireRef.current('block', 0.4);
          break;
        case 'read':
        case 'stance_block':
        case 'shield':
          setParticle({ id: Math.random(), type: 'block', x: side === 'right' ? 0.3 : 0.7 });
          pushFloat({ type: 'block', text: '🛡️ BLOCKED', side: side === 'right' ? 'left' : 'right' });
          fireRef.current('block', 0.5);
          break;
        case 'status':
          pushFloat({
            type: 'status', text: `${e.status.toUpperCase()}!`,
            side: side === 'right' ? 'left' : 'right',
          });
          setParticle({
            id: Math.random(),
            type: e.status === 'burn' ? 'burn' : 'bleed',
            x: side === 'right' ? 0.3 : 0.7,
          });
          break;
        case 'dot':
          pushFloat({ type: 'dmg', text: `−${e.dmg}`, side });
          break;
        case 'stunned':
          pushFloat({ type: 'status', text: '😵 STUNNED', side });
          fireRef.current('block', 0.8);
          break;
        case 'defend':
          setParticle({ id: Math.random(), type: 'magic', x: side === 'right' ? 0.7 : 0.3 });
          break;
        default:
          break;
      }
    }
  }, [pushFloat]);

  const playFXRef = useRef(playFX);
  playFXRef.current = playFX;

  /* ════════════════════════════════════════════
     INITIAL LOAD — runs once per fight id
     ════════════════════════════════════════════ */
  useEffect(() => {
    let alive = true;
    setState(null);
    setResult(null);
    setChat([]);
    joinedRef.current = false;

    api.get(`/fights/${id}`)
      .then(r => { if (alive) setState(r.data); })
      .catch(e => {
        if (!alive) return;
        toast.error(e.message);
        nav('/arena', { replace: true });
      });

    api.get(`/chat/fight/${id}`)
      .then(r => { if (alive) setChat(r.data || []); })
      .catch(() => {});

    return () => { alive = false; };
  }, [id, nav]);

  /* ════════════════════════════════════════════
     SOCKET — attaches once per fight id
     ════════════════════════════════════════════ */
  useEffect(() => {
    const s = getSocket();
    if (!s || joinedRef.current) return;

    joinedRef.current = true;
    s.emit('fight:join', id);
    s.emit('join', `fight:${id}`);

    const onUpdate = (d) => {
      setState(prev => ({
        ...d,
        isP1: prev?.isP1 ?? d.isP1,
        myTurn: (d.turn === 1 ? d.p1.user_id : d.p2.user_id) === userId,
      }));
      if (d.lastEvents) playFXRef.current(d.lastEvents, d);
      if (d.finished) setResult(d.rewards ?? { winnerId: d.winner_id });
    };

    const onFinish = ({ winnerId, rewards }) => {
      setResult({ ...rewards, winnerId });
      if (rewards?.drop && winnerId === userId) {
        setTimeout(() => {
          setDrop(rewards.drop);
          fireRef.current('drop', 1.5);
          setParticle({ id: Math.random(), type: 'drop', x: 0.5 });
        }, 900);
      }
      qc.invalidateQueries({ queryKey: ['hero'] });
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['pet'] });
    };

    const onChat = (m) => setChat(c => [...c, m]);

    const onTaunt = ({ from, emoji }) =>
      pushFloat({ type: 'taunt', text: emoji, side: from === userId ? 'left' : 'right' });

    s.on('fight:update', onUpdate);
    s.on('fight:finished', onFinish);
    s.on('chat:message', onChat);
    s.on('fight:taunt', onTaunt);

    return () => {
      s.off('fight:update', onUpdate);
      s.off('fight:finished', onFinish);
      s.off('chat:message', onChat);
      s.off('fight:taunt', onTaunt);
      s.emit('leave', `fight:${id}`);
      joinedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* auto-scroll log */
  useEffect(() => {
    logRef.current?.scrollTo({ top: 9e9, behavior: 'smooth' });
  }, [state?.log?.length, chat.length]);

  /* ════════════════════════════════════════════
     ACTIONS
     ════════════════════════════════════════════ */
  async function play() {
    if (!move || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/fights/${id}/move`, {
        move, targetZone: target, guardZone: guard,
      });
      setState(data);
      playFXRef.current(data.lastEvents || [], data);
      if (data.finished) {
        setResult(data.rewards ?? { winnerId: data.winner_id });
        if (data.rewards?.drop) setTimeout(() => setDrop(data.rewards.drop), 900);
      }
      setMove(null);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  function sendChat() {
    const s = getSocket();
    if (!s || !msg.trim()) return;
    s.emit('chat:send', { roomType: 'fight', roomId: id, body: msg });
    setMsg('');
  }

  function taunt(emoji) {
    getSocket()?.emit('fight:taunt', { fightId: id, emoji });
  }

  async function forfeit() {
    if (!confirm('Forfeit this duel?')) return;
    try {
      await api.post(`/fights/${id}/forfeit`);
      nav('/arena');
    } catch (e) { toast.error(e.message); }
  }

  if (!state || !meta) return <SkeletonList n={4} h="h-40" />;

  const me = state.isP1 ? state.p1 : state.p2;
  const opp = state.isP1 ? state.p2 : state.p1;
  const myT = themeOf(me.class_key);
  const oppT = themeOf(opp.class_key);
  const myMoves = meta.classes[me.class_key]?.moves.map(k => meta.moves[k]) || [];
  const over = state.state === 'finished' || !!result;
  const ultReady = me.focus >= 100;

  return (
    <div className="max-w-lg mx-auto space-y-3 animate-fade-up">
      <UltimateOverlay show={!!ult} name={ult?.name} heroImg={ult?.img}
        classKey={ult?.classKey} onDone={() => setUlt(null)} />
      <ItemDropReveal drop={drop} onClose={() => setDrop(null)} />

      {/* ═══════ BATTLEFIELD ═══════ */}
      <div className="card-arena overflow-hidden relative" style={shakeStyle}>
        <ArenaFloor classKey={me.class_key} className="p-4" showCrowd>
          <ParticleLayer trigger={particle} classKey={me.class_key} />
          <FloatingNumbers items={floats} />
          <SlashFX slash={slash} classKey={me.class_key} />
          <FlashLayer />
          <ComboMeter combo={combo} classKey={me.class_key} />

          {/* header */}
          <div className="relative flex items-center justify-between text-[10px]
            font-display font-black mb-3 z-10">
            <span className="px-2 py-0.5 rounded-lg"
              style={{ background: 'rgba(232,200,116,.14)', color: ARENA.arenaText }}>
              ROUND {state.round}
            </span>
            {state.stake > 0 && (
              <motion.span className="px-2 py-0.5 rounded-lg"
                style={{ background: 'rgba(232,200,116,.2)', color: ARENA.arenaText }}
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ duration: 1.6, repeat: Infinity }}>
                💎 {state.stake * 2}
              </motion.span>
            )}
            <span className="px-2 py-0.5 rounded-lg"
              style={{
                background: over ? 'rgba(232,200,116,.14)'
                  : state.myTurn ? ARENA.goldFrame : 'rgba(232,200,116,.14)',
                color: state.myTurn && !over ? '#fff' : ARENA.arenaText,
              }}>
              {over ? 'FINISHED' : state.myTurn ? 'YOUR TURN' : 'THEIR TURN'}
            </span>
          </div>

          {/* fighters */}
          <div className="relative grid grid-cols-[1fr_auto_1fr] gap-2 items-center z-10">
            <Fighter f={me} mine active={state.myTurn && !over} theme={myT} />
            <div className="text-center">
              <motion.div className="w-11 h-11 rounded-full grid place-items-center
                font-display font-black text-sm"
                style={{
                  background: state.myTurn && !over ? myT.hex : 'rgba(255,255,255,.12)',
                  color: '#fff',
                  boxShadow: state.myTurn && !over ? `0 0 20px -2px ${myT.hex}` : 'none',
                }}
                animate={state.myTurn && !over ? { rotate: [0, 8, -8, 0] } : {}}
                transition={{ duration: 2, repeat: Infinity }}>
                VS
              </motion.div>
            </div>
            <Fighter f={opp} active={!state.myTurn && !over} theme={oppT} />
          </div>
        </ArenaFloor>

        {/* LOG */}
        <div ref={logRef}
          className="h-28 overflow-y-auto px-3 py-2 text-[11px] font-mono space-y-0.5"
          style={{ background: '#12100c', color: 'rgba(232,220,200,.82)' }}>
          {state.log?.map((l, i) => (
            <motion.p key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
              className={
                l.text?.includes('CRITICAL') ? 'text-amber-300 font-bold'
                  : l.text?.includes('🐾') ? 'text-emerald-300'
                  : l.text?.includes('dodges') ? 'opacity-60'
                  : l.text?.includes('BLOCK') || l.text?.includes('read') ? 'text-sky-300'
                  : ''}>
              {l.text}
            </motion.p>
          ))}
          {chat.map(c => (
            <p key={c.id} className="text-fuchsia-300">💬 <b>{c.username}:</b> {c.body}</p>
          ))}
        </div>
      </div>

      {/* ═══════ RESULT ═══════ */}
      {over && result && (
        <motion.div initial={{ scale: .85, opacity: 0, y: 24 }} animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 220, damping: 18 }}
          className="card-arena p-6 text-center relative overflow-hidden">
          {result.winnerId === userId && (
            <motion.div className="absolute inset-0 opacity-25"
              style={{ background: 'conic-gradient(from 0deg, transparent, rgba(232,200,116,.6), transparent)' }}
              animate={{ rotate: 360 }}
              transition={{ duration: 4, repeat: Infinity, ease: 'linear' }} />
          )}

          <motion.div animate={{ y: [0, -10, 0], rotate: [0, -8, 8, 0] }}
            transition={{ duration: 1.4, repeat: 2 }}>
            <Trophy size={44} className="mx-auto mb-2 relative" style={{ color: ARENA.arenaText }} />
          </motion.div>

          <h2 className="font-display text-3xl font-extrabold relative neon-glow-text"
            style={{ color: ARENA.arenaText }}>
            {!result.winnerId ? 'DRAW' : result.winnerId === userId ? 'VICTORY!' : 'DEFEAT'}
          </h2>

          {result.winnerPoints !== undefined && (
            <div className="mt-2.5 space-y-1 relative text-white">
              <p className="font-display font-bold">
                {result.winnerId === userId
                  ? `+${result.winnerPoints} 💎 · +${result.winnerXp} XP`
                  : `+${result.loserPoints} 💎 · +${result.loserXp} XP`}
              </p>
              {(result.winnerId === userId ? result.petXpWinner : result.petXpLoser) > 0 && (
                <p className="text-xs opacity-80">
                  🐾 +{result.winnerId === userId ? result.petXpWinner : result.petXpLoser} companion XP
                </p>
              )}
              <p className="text-sm opacity-85 font-bold">
                ELO {(state.isP1 ? result.p1EloDelta : result.p2EloDelta) > 0 ? '+' : ''}
                {state.isP1 ? result.p1EloDelta : result.p2EloDelta}
              </p>
              {result.capped && (
                <p className="text-[11px] opacity-70 font-semibold">
                  ⏳ Daily arena point cap reached — XP only
                </p>
              )}
            </div>
          )}

          <div className="flex gap-2 mt-5 relative">
            <Link to="/arena" className="btn-ghost flex-1">Arena</Link>
            <Link to="/hero" className="btn-primary flex-1">Upgrade</Link>
          </div>
        </motion.div>
      )}

      {/* ═══════ CONTROLS ═══════ */}
      {!over && (
        <>
          <div className="card p-3">
            <p className="label">1 · Choose your move</p>
            <div className="grid grid-cols-3 gap-1.5">
              {myMoves.map(m => {
                const noFocus = m.focusCost > 0 && me.focus < m.focusCost;
                const cd = me.cooldowns?.[m.key] > 0;
                const dis = !state.myTurn || noFocus || cd;
                const isUlt = m.key === 'ultimate';
                const sel = move === m.key;

                return (
                  <motion.button key={m.key} onClick={() => setMove(m.key)} disabled={dis}
                    whileTap={{ scale: 0.92 }}
                    animate={isUlt && ultReady && !dis
                      ? { boxShadow: ['0 0 0 rgba(0,0,0,0)', `0 0 22px ${myT.hex}`, '0 0 0 rgba(0,0,0,0)'] }
                      : {}}
                    transition={{ duration: 1.4, repeat: Infinity }}
                    className={`relative p-2 rounded-xl border-2 text-center overflow-hidden
                      ${dis ? 'opacity-40' : ''}`}
                    style={sel
                      ? { borderColor: myT.hex, background: `${myT.hex}12` }
                      : isUlt && ultReady
                        ? { borderColor: myT.glow, background: `${myT.hex}0a` }
                        : { borderColor: 'var(--border)', background: 'var(--surface)' }}>
                    <motion.div className="text-xl"
                      animate={sel ? { scale: [1, 1.25, 1] } : {}}
                      transition={{ duration: .35 }}>
                      {m.icon}
                    </motion.div>
                    <p className="text-[10px] font-display font-black mt-0.5 leading-tight">{m.name}</p>
                    <p className="text-[9px] font-bold" style={{ color: 'var(--text-muted)' }}>
                      {m.focusCost > 0 ? `🌀${m.focusCost}`
                        : m.stamina > 0 ? `⚡${m.stamina}` : `+${-m.stamina}⚡`}
                    </p>
                    {cd && (
                      <span className="absolute inset-0 grid place-items-center font-black text-xs"
                        style={{ background: 'rgba(251,250,247,.8)' }}>
                        {me.cooldowns[m.key]}
                      </span>
                    )}
                  </motion.button>
                );
              })}
            </div>
            <AnimatePresence>
              {move && (
                <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="text-[11px] mt-2 font-semibold" style={{ color: 'var(--text-muted)' }}>
                  {meta.moves[move]?.desc}
                </motion.p>
              )}
            </AnimatePresence>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <ZonePicker title="2 · Strike" value={target} onChange={setTarget}
              color={myT.hex} disabled={!state.myTurn} />
            <ZonePicker title="3 · Guard" value={guard} onChange={setGuard}
              color="#8b5cf6" disabled={!state.myTurn} icon={Shield} />
          </div>

          <motion.button onClick={play} disabled={!move || !state.myTurn || busy}
            whileTap={{ scale: 0.96 }}
            animate={move && state.myTurn
              ? { boxShadow: [`0 8px 24px ${myT.hex}44`, `0 8px 38px ${myT.hex}88`, `0 8px 24px ${myT.hex}44`] }
              : {}}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-full py-4 rounded-2xl text-base font-display font-black text-white
              transition disabled:opacity-50"
            style={{ background: `linear-gradient(135deg, ${myT.hex}, ${myT.glow})` }}>
            {busy ? 'Resolving…'
              : !state.myTurn ? '⏳ Waiting for opponent'
              : !move ? 'Pick a move'
              : `⚔️ ${target.toUpperCase()} / 🛡️ ${guard.toUpperCase()}`}
          </motion.button>

          <div className="card p-2 flex gap-1.5">
            {['😈', '🔥', '😂', '💪', '🫡'].map(e => (
              <motion.button key={e} whileTap={{ scale: 1.35 }} onClick={() => taunt(e)}
                className="w-9 h-9 rounded-lg text-lg"
                style={{ background: 'var(--surface-alt)' }}>{e}</motion.button>
            ))}
            <input className="input !py-2 flex-1 text-sm" placeholder="Trash talk…"
              value={msg} onChange={e => setMsg(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendChat()} />
            <button onClick={sendChat} className="btn-dark btn-sm"><Send size={14} /></button>
          </div>

          <button onClick={forfeit}
            className="w-full text-xs font-bold py-2 flex items-center justify-center gap-1"
            style={{ color: 'var(--text-muted)' }}>
            <Flag size={12} /> Forfeit
          </button>
        </>
      )}
    </div>
  );
}

/* ============================================================
   FIGHTER
   ============================================================ */
function Fighter({ f, mine, active, theme }) {
  const hpPct = (f.hp / f.maxHp) * 100;
  const stPct = (f.stamina / f.maxStamina) * 100;
  const dead = f.hp === 0;

  return (
    <motion.div className="text-center"
      animate={active ? { scale: 1.05, y: -3 } : { scale: 1, y: 0, opacity: .85 }}
      transition={{ type: 'spring', stiffness: 260, damping: 20 }}>

      <div className="relative w-20 h-28 mx-auto rounded-xl overflow-hidden"
        style={{
          padding: 2,
          background: active ? ARENA.goldFrame : 'rgba(255,255,255,.15)',
          boxShadow: active ? `0 0 24px -4px ${theme.hex}` : 'none',
        }}>
        <div className="w-full h-full rounded-[10px] overflow-hidden relative bg-black/40">
          <motion.div className="w-full h-full"
            animate={hpPct < 25 && !dead ? { scale: [1, 1.04, 1] } : {}}
            transition={{ duration: .85, repeat: Infinity }}>
            {f.avatar
              ? <img src={f.avatar} alt="" className="w-full h-full object-cover object-top" />
              : <div className="w-full h-full grid place-items-center text-2xl">{theme.icon}</div>}
          </motion.div>
          <StatusAura statuses={f.statuses} />
          <KOEffect show={dead} />
        </div>

        <div className="absolute top-1 left-1 px-1 rounded text-[8px] font-black z-10"
          style={{ background: `${theme.hex}dd`, color: '#fff' }}>
          {theme.icon}
        </div>
      </div>

      {/* pet */}
      {f.pet && (
        <motion.div className="w-8 h-8 rounded-lg overflow-hidden mx-auto -mt-2.5 relative z-10"
          style={{ padding: 1.5, background: ARENA.goldFrame }}
          animate={{ y: [0, -2, 0] }}
          transition={{ duration: 2.4, repeat: Infinity }}
          title={`${f.pet.name} · Lv${f.pet.level}`}>
          <div className="w-full h-full rounded overflow-hidden bg-black/40">
            <img src={f.pet.image} alt="" className="w-full h-full object-cover" />
          </div>
        </motion.div>
      )}

      <p className="font-display font-black text-[11px] mt-1.5 truncate"
        style={{ color: ARENA.arenaText }}>{f.name}</p>
      <p className="text-[9px] font-bold" style={{ color: ARENA.arenaTextSoft }}>
        L{f.level} · {f.elo}{f.gearScore ? ` · ${f.gearScore}GS` : ''}
      </p>

      <GearChips equipment={f.equipment} />

      <div className="mt-1.5 space-y-1">
        <HPBar hp={f.hp} maxHp={f.maxHp} label={`${f.hp}/${f.maxHp}`} />
        <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,.45)' }}>
          <motion.div className="h-full"
            style={{ background: 'linear-gradient(90deg,#38bdf8,#3b82f6)' }}
            animate={{ width: `${stPct}%` }} transition={{ duration: .3 }} />
        </div>
        <FocusOrb focus={f.focus} classKey={f.class_key} />
      </div>

      {f.statuses?.length > 0 && (
        <div className="flex gap-0.5 justify-center mt-1 flex-wrap">
          {f.statuses.map((s, i) => (
            <motion.span key={i} animate={{ scale: [1, 1.18, 1] }}
              transition={{ duration: 1.1, repeat: Infinity }}
              className="text-[8px] px-1 rounded font-black text-white"
              style={{ background: 'rgba(239,68,68,.85)' }}>
              {{ bleed: '🩸', burn: '🔥', stun: '😵', slow: '🐌', shield: '🛡️' }[s.status]}{s.turns}
            </motion.span>
          ))}
        </div>
      )}
    </motion.div>
  );
}

/* ============================================================
   ZONE PICKER
   ============================================================ */
function ZonePicker({ title, value, onChange, color, disabled, icon: Icon }) {
  return (
    <div className="card p-3">
      <p className="label flex items-center gap-1">
        {Icon && <Icon size={11} />}{title}
      </p>
      <div className="space-y-1">
        {ZONES.map(z => {
          const sel = value === z.key;
          return (
            <motion.button key={z.key} onClick={() => onChange(z.key)} disabled={disabled}
              whileTap={{ scale: .93 }}
              className={`w-full flex items-center gap-2 p-1.5 rounded-lg text-left transition
                ${disabled ? 'opacity-50' : ''}`}
              style={sel
                ? { background: color, color: '#fff', boxShadow: `0 4px 14px ${color}55` }
                : { background: 'var(--surface-alt)', color: 'var(--text-soft)' }}>
              <motion.span className="text-base"
                animate={sel ? { scale: [1, 1.28, 1] } : {}}>{z.icon}</motion.span>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-display font-black">{z.label}</p>
                <p className="text-[9px] opacity-70 font-semibold truncate">{z.hint}</p>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}